/* eslint-disable max-lines */
/**
 * Deep Explorer generic content-sidebar panel pieces.
 *
 * Layout: Rail-default + contextual hero + strips (design direction #4 + #2)
 *
 * Structure (left-to-right inside the panel):
 *   Main area (flex-1):
 *     Hero section   — full expanded view of the active hero (Plan/Activity/Files)
 *     Strip × 2      — 1-row clickable summary for each non-hero section
 *   Rail (48px):
 *     Right-hand column with icon-buttons that switch the active hero
 */
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { PlanDecision } from "./plan-decision-types"
import type { Document } from "../../../../api/models"
import type { CitationPreviewState } from "../../../../features/chat/contexts/citation-preview-shared"

import {
  BookOpen,
  ChevronUp,
  Download,
  ExternalLink,
  FileOutput,
  FileText,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  X as XIcon,
  Zap,
} from "@/components/icons"

import { ActiveWorkersRail } from "./ActiveWorkersRail"
import { ActivityLog } from "./ActivityLog"
import { AskUserModal } from "./AskUserModal"
import { BtwInput } from "./BtwInput"
import {
  mergeSourceCards,
  resolveCitationWithSources,
  sidebarPreviewArguments,
  sourceCardsFromSavedDocuments,
} from "./source-card-utils"
import {
  citationActivityFromSaved,
  collectSourceCards,
  selectDeliverableCitationScope,
} from "./source-ribbon-utils"
import { SourceRibbon } from "./SourceRibbon"
import { TodoList } from "./TodoList"
import { VirtualFileBrowser } from "./VirtualFileBrowser"
import { VirtualFileViewer } from "./VirtualFileViewer"
import { createDocumentApi, updateDocumentApi } from "../../../../api/api"
import { Button } from "../../../../components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../../components/ui/tooltip"
import { CitationViewerContent } from "../../../../features/chat/components/citation-viewer-content"
import { resolveCitationPreview } from "../../../../features/chat/contexts/citation-preview-utils"
import { useCitationPreview } from "../../../../features/chat/contexts/use-citation-preview"
import { logger } from "../../../../lib/logger"
import { cn } from "../../../../lib/utils"
import { SettingsService } from "../../../../services/settings-service"
import {
  type DeepActivityEvent,
  type DeepExplorerSessionState,
  type DeepTodo,
  getDeepStateForSession,
  useAIExplorerStore,
} from "../../../../stores/ai-explorer-store"
import { openCanvasInSidebar } from "../../../canvas/utils/canvas-sidebar"
import { buildCanvasContent } from "../../utils/buildCanvasContent"
import { categorizeFiles } from "../../utils/categorizeFiles"
import { exportScratchpad } from "../../utils/exportScratchpad"

// ── Types ─────────────────────────────────────────────────────────────────────

type HeroId = "plan" | "activity" | "files" | "viewer"

/** Bundle held by the Viewer hero — a resolved citation plus display title. */
type ViewerPreview = {
  state: CitationPreviewState
  title: string
  citation: string
  citationSection: string
  /** Documents the inline resolver used. The sidebar must get the same list. */
  documents?: [string, Document][]
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function readJsonFromStorage<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

function writeJsonToStorage(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // localStorage may be unavailable or full - silently ignore.
  }
}

function getTokenBudgetClassName(usageRatio: number) {
  if (usageRatio > 0.85) {
    return "bg-destructive-ink"
  }
  if (usageRatio > 0.6) {
    return "bg-warning-ink"
  }
  return "bg-primary-ink"
}

function getSubagentDispatchStats(events: DeepActivityEvent[]) {
  const started = new Set<string>()
  const completed = new Set<string>()

  for (const event of events) {
    if (event.tool !== "task" || event.source === "deep_explorer" || !event.dispatch_id) {
      continue
    }
    const key = `${event.source}:${event.dispatch_id}`
    if (event.event === "tool_start") {
      started.add(key)
    } else {
      completed.add(key)
    }
  }

  let active = 0
  for (const key of started) {
    if (!completed.has(key)) active += 1
  }

  return { active, used: started.size }
}

// ── Onboarding overlay (idle until a run starts, or user taps "Got it") ─────

function OnboardingOverlay({ onDismiss }: { onDismiss: () => void }) {
  const { t } = useTranslation()
  return (
    <div
      aria-label={t("deepExplorer.onboarding_aria")}
      aria-modal="true"
      className="bg-card/95 absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 p-6 text-center backdrop-blur-sm"
      role="dialog"
    >
      <Sparkles className="text-primary size-8" />
      <h2 className="text-foreground text-base font-semibold">
        {t("deepExplorer.onboarding_title")}
      </h2>
      <ul className="text-muted-foreground space-y-2 text-left text-sm">
        <li className="flex items-start gap-2">
          <span className="text-primary mt-0.5">▣</span>
          <span>
            <strong className="text-foreground">{t("deepExplorer.onboarding_plan")}</strong> -{" "}
            {t("deepExplorer.onboarding_plan_desc")}
          </span>
        </li>
        <li className="flex items-start gap-2">
          <span className="text-primary mt-0.5">📄</span>
          <span>
            <strong className="text-foreground">{t("deepExplorer.onboarding_scratchpad")}</strong> -{" "}
            {t("deepExplorer.onboarding_scratchpad_desc")}
          </span>
        </li>
        <li className="flex items-start gap-2">
          <span className="text-primary mt-0.5">⬇</span>
          <span>
            <strong className="text-foreground">{t("deepExplorer.onboarding_export")}</strong> -{" "}
            {t("deepExplorer.onboarding_export_desc")}
          </span>
        </li>
        <li className="flex items-start gap-2">
          <span className="text-primary mt-0.5">⌨</span>
          <span>
            <strong className="text-foreground">{t("deepExplorer.onboarding_shortcut")}</strong> -{" "}
            {t("deepExplorer.onboarding_shortcut_prefix")}{" "}
            <code className="bg-muted rounded px-1 font-mono text-xs">/deep your question</code>{" "}
            {t("deepExplorer.onboarding_shortcut_suffix")}
          </span>
        </li>
      </ul>
      <p className="text-muted-foreground text-xs">
        {t("deepExplorer.onboarding_disable_anytime")}
      </p>
      <Button size="sm" variant="default" onClick={onDismiss}>
        {t("deepExplorer.onboarding_dismiss")}
      </Button>
    </div>
  )
}

// ── Narration toggle ──────────────────────────────────────────────────────────

function NarrationToggle({ sessionType }: { sessionType: string }) {
  const { t } = useTranslation()
  const store = useAIExplorerStore(sessionType)
  const enabled = store((s) => s.enableNarration)
  const setEnabled = store((s) => s.setEnableNarration)
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          aria-label={t("deepExplorer.narration_toggle")}
          aria-pressed={enabled}
          type="button"
          className={cn(
            "flex size-10 items-center justify-center rounded-md transition-colors duration-150",
            "text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
            enabled && "text-primary bg-primary/10"
          )}
          onClick={() => setEnabled(!enabled)}
        >
          <MessageCircle className="size-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="left">
        {enabled ? t("deepExplorer.narration_on") : t("deepExplorer.narration_off")}
      </TooltipContent>
    </Tooltip>
  )
}

// ── Fact-check toggle ─────────────────────────────────────────────────────────

function FactCheckToggle({ sessionType }: { sessionType: string }) {
  const { t } = useTranslation()
  const store = useAIExplorerStore(sessionType)
  const enabled = store((s) => s.enableFactCheck)
  const setEnabled = store((s) => s.setEnableFactCheck)
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          aria-label={t("deepExplorer.fact_check_toggle")}
          aria-pressed={enabled}
          type="button"
          className={cn(
            "flex size-10 items-center justify-center rounded-md transition-colors duration-150",
            "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
            enabled
              ? "bg-success/10 text-success hover:bg-success/20"
              : "text-muted-foreground hover:bg-accent hover:text-foreground"
          )}
          onClick={() => setEnabled(!enabled)}
        >
          <ShieldCheck className="size-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="left">
        {enabled ? t("deepExplorer.fact_check_on") : t("deepExplorer.fact_check_off")}
      </TooltipContent>
    </Tooltip>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

type DeepExplorerPanelCallbacks = {
  sessionType: string
  sessionTitle?: string
  /**
   * Citation section (search-service id) — used by SourceCards and deliverable
   * citation clicks so `openPreview` can resolve the right indexed-file route.
   * Defaults to `sessionType` when omitted, matching the regular chat flow
   * where `defaultSearchServiceId` falls back to `sessionType`.
   */
  citationSection?: string
  onPlanDecision?: (
    decision: PlanDecision,
    extra?: { todos?: DeepTodo[]; feedback?: string; editedContent?: string }
  ) => void
  onAskUserResponse?: (
    answers: Record<string, string | string[]>,
    targetSessionId?: string | null,
    askId?: string | null
  ) => void
  /** UI-5: invoked when the user clicks "Rewind to plan" during an active run. */
  onRewindToPlan?: () => void
  /** UI-6: dispatch a side note to the running agent. Returns true on
   *  successful send so the input can flash a "sent" affordance. */
  onSendUserNote?: (note: string) => boolean
  /** UI-7: detach an approved plan to the runs panel. Receives the same
   *  edited-plan bundle the regular approve path uses, plus the side-panel
   *  takes care of capturing the session into the runs store. */
  onApproveInBackground?: (extra?: { editedContent?: string }) => void
  /**
   * Phase 5.2: scope the activity log to a specific mission_id. Set by the
   * Workspace layout when the user clicks "View trace" on an artifact.
   *
   * NOTE: `DeepActivityEvent` does not yet carry a `mission_id` field — and
   * today the artifact contract uses `mission_id ≈ thread_id`, which means
   * every event in the current session already belongs to "the mission".
   * Filtering is therefore a no-op for the moment: we render every event
   * unchanged when `missionFilter` is set, but still surface the banner so
   * the affordance ships before mission scoping gets finer-grained. When
   * events grow a real `mission_id` (per-turn UUID), update the filter
   * predicate below — the banner is already wired.
   *
   * Legacy path: omit this prop and the panel renders identically to before.
   */
  missionFilter?: string | null
  /**
   * Phase 1 (workspace-focus): when rendered inside the workspace activity
   * peek, the Plan rail/strip/hero is suppressed because the plan artifact
   * in the center workspace owns plan approval. Default `false` keeps the
   * legacy ContentSidebar deep panel rendering all three sections (Plan,
   * Activity, Files) verbatim.
   */
  inWorkspaceMode?: boolean
}

export function DeepExplorerPanelTitle({ sessionType }: { sessionType: string }) {
  const { t } = useTranslation()
  const explorerStore = useAIExplorerStore(sessionType)
  const { todos, subagentActive, token_budget, activityLog } = explorerStore((state) =>
    getDeepStateForSession(state, state.currentSessionId)
  )
  const inProgressCount = todos.filter((todo) => todo.status === "in_progress").length
  const subagentStats = useMemo(() => getSubagentDispatchStats(activityLog), [activityLog])
  const tokenBudgetUsage =
    token_budget && token_budget.total > 0 ? token_budget.used / token_budget.total : 0
  const tokenBudgetPercent = Math.round(tokenBudgetUsage * 100)
  const hasTokenBudgetUsage = Boolean(
    token_budget && token_budget.total > 0 && token_budget.used > 0
  )

  return (
    <span className="flex min-w-0 items-center gap-x-2 gap-y-1">
      <Sparkles className="text-primary size-4 shrink-0" />
      <span className="truncate">{t("deepExplorer.panel_title")}</span>
      <span className="border-primary/30 bg-primary/10 text-primary shrink-0 rounded-full border px-1.5 py-px text-[10px] leading-none font-semibold">
        {t("deepExplorer.beta_badge", { defaultValue: "Beta" })}
      </span>
      {inProgressCount > 0 && (
        <span className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-xs tabular-nums">
          {inProgressCount}
        </span>
      )}
      {subagentStats.used > 0 && (
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-xs tabular-nums",
            subagentStats.active > 0
              ? "bg-primary/10 text-primary animate-pulse motion-reduce:animate-none"
              : "bg-muted text-muted-foreground"
          )}
          title={t("deepExplorer.subagent_count_title", {
            active: subagentStats.active,
            used: subagentStats.used,
            defaultValue: "{{active}} active, {{used}} used",
          })}
        >
          {subagentStats.active > 0
            ? t("deepExplorer.subagent_count_active", {
                active: subagentStats.active,
                used: subagentStats.used,
                defaultValue: "{{active}}/{{used}} agents",
              })
            : t("deepExplorer.subagent_count_used", {
                used: subagentStats.used,
                defaultValue: "{{used}} agents",
              })}
        </span>
      )}
      {subagentActive && (
        <span className="text-muted-foreground animate-pulse truncate text-xs motion-reduce:animate-none">
          {t("deepExplorer.subagent_working", { agent: subagentActive })}
        </span>
      )}
      {hasTokenBudgetUsage && token_budget && (
        <span
          aria-label={t("deepExplorer.token_budget_aria", { pct: tokenBudgetPercent })}
          aria-valuemax={token_budget.total}
          aria-valuemin={0}
          aria-valuenow={token_budget.used}
          className="ml-1 flex shrink-0 items-center gap-1"
          role="progressbar"
          title={t("deepExplorer.budget_used", { pct: tokenBudgetPercent })}
        >
          <span className="bg-muted block h-1.5 w-10 overflow-hidden rounded-full">
            <span
              className={cn(
                "block h-full rounded-full transition-[width,background-color] duration-700",
                getTokenBudgetClassName(tokenBudgetUsage)
              )}
              style={{
                width: `${Math.min(100, tokenBudgetPercent)}%`,
              }}
            />
          </span>
        </span>
      )}
    </span>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function DeepExplorerPanelActions({ sessionType }: { sessionType: string }) {
  // Narration and fact-check toggles now live in the rail inside DeepExplorerPanelContent.
  // This exported function is kept for contract compatibility; callers render it in the
  // sidebar header and get null (no header-level action buttons).
  return null
}

// ── Rail icon button ──────────────────────────────────────────────────────────

interface RailButtonProps {
  /** Omit for an action button; it then carries no pressed state. */
  active?: boolean
  label: string
  onClick: () => void
  children: ReactNode
  pulseDot?: boolean
  badge?: string | number | null
}

function RailButton({ active, label, onClick, children, pulseDot, badge }: RailButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          aria-label={label}
          aria-pressed={active}
          type="button"
          className={cn(
            "relative flex size-10 items-center justify-center rounded-md transition-colors duration-150",
            "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
            active
              ? "text-primary bg-primary/10"
              : "text-muted-foreground hover:bg-accent hover:text-foreground"
          )}
          onClick={onClick}
        >
          {children}
          {pulseDot && (
            <span className="animate-subtle-pulse bg-primary absolute top-1.5 right-1.5 size-1.5 rounded-full motion-reduce:animate-none" />
          )}
          {badge != null && Number(badge) > 0 && (
            <span className="border-foreground bg-primary text-primary-foreground absolute -top-0.5 -right-0.5 flex size-3.5 items-center justify-center rounded-full border text-[10px] font-bold tabular-nums">
              {Number(badge) > 9 ? "9+" : badge}
            </span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="left">{label}</TooltipContent>
    </Tooltip>
  )
}

// ── Plan-ring icon (conic progress) ──────────────────────────────────────────

interface PlanRingProps {
  done: number
  total: number
  active: boolean
  proposed: boolean
}

function PlanRing({ done, total, active, proposed }: PlanRingProps) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0
  const showRing = total > 0 || proposed
  if (!showRing) {
    return (
      <span className="flex size-6 items-center justify-center">
        <Sparkles className="size-4" />
      </span>
    )
  }
  // SVG donut keeps the ring crisp and pixel-centered. Conic-gradient + inset
  // overlay produced a visible 12 o'clock seam and an off-center inner disc
  // when the parent grew (bug 4 in bugerport_deepagents.pdf).
  const radius = 9
  const circumference = 2 * Math.PI * radius
  const dash = (pct / 100) * circumference
  return (
    <svg
      aria-hidden="true"
      className={cn("block size-6", active ? "text-primary" : "text-muted-foreground")}
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        cx="12"
        cy="12"
        r={radius}
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeWidth="3"
      />
      <circle
        cx="12"
        cy="12"
        r={radius}
        stroke="currentColor"
        strokeDasharray={`${dash} ${circumference}`}
        strokeLinecap="round"
        strokeWidth="3"
        transform="rotate(-90 12 12)"
      />
    </svg>
  )
}

// ── Token-bar (4×24 vertical) ─────────────────────────────────────────────────

interface TokenBarProps {
  usageRatio: number
  percent: number
  label: string
}

function getTokenBarColorClass(usageRatio: number): string {
  if (usageRatio > 0.85) return "bg-destructive-ink"
  if (usageRatio > 0.6) return "bg-warning-ink"
  return "bg-primary-ink"
}

function TokenBar({ usageRatio, percent, label }: TokenBarProps) {
  const colorClass = getTokenBarColorClass(usageRatio)

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          aria-label={label}
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={percent}
          className="bg-muted flex h-6 w-1 overflow-hidden rounded-full"
          role="progressbar"
        >
          <span
            style={{ height: `${Math.min(100, percent)}%` }}
            className={cn(
              "w-full self-end rounded-full transition-[height,background-color] duration-700",
              colorClass
            )}
          />
        </span>
      </TooltipTrigger>
      <TooltipContent side="left">{label}</TooltipContent>
    </Tooltip>
  )
}

// ── Strip (1-row collapsed section summary) ───────────────────────────────────

interface StripProps {
  onClick: () => void
  children: ReactNode
  pulseDot?: boolean
  trailingAction?: ReactNode
}

function Strip({ onClick, children, pulseDot, trailingAction }: StripProps) {
  return (
    <div
      className={cn(
        "border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground",
        "flex h-9 w-full shrink-0 items-center gap-2 border-t px-3 text-xs transition-colors duration-150"
      )}
    >
      <button
        className="focus-visible:ring-ring flex h-full min-w-0 flex-1 items-center gap-2 text-left focus-visible:ring-2 focus-visible:outline-none"
        type="button"
        onClick={onClick}
      >
        <span className="relative flex min-w-0 flex-1 items-center gap-2 truncate">
          {pulseDot && (
            <span className="animate-subtle-pulse bg-primary size-1.5 shrink-0 rounded-full motion-reduce:animate-none" />
          )}
          {children}
        </span>
        <ChevronUp className="text-muted-foreground/60 size-3.5 shrink-0" />
      </button>
      {trailingAction}
    </div>
  )
}

// ── Hero section wrappers ─────────────────────────────────────────────────────

interface HeroSectionProps {
  children: ReactNode
  className?: string
}

function HeroSection({ children, className }: HeroSectionProps) {
  return (
    <div
      className={cn(
        "animate-in fade-in-0 slide-in-from-top-1 duration-200 ease-out",
        "flex min-h-0 flex-1 flex-col overflow-hidden",
        className
      )}
    >
      {children}
    </div>
  )
}

// ── Main content ──────────────────────────────────────────────────────────────

export function DeepExplorerPanelContent({
  sessionType,
  sessionTitle,
  citationSection,
  onPlanDecision,
  onAskUserResponse,
  onRewindToPlan,
  onSendUserNote,
  onApproveInBackground,
  missionFilter,
  inWorkspaceMode = false,
}: DeepExplorerPanelCallbacks) {
  const { t } = useTranslation()
  const explorerStore = useAIExplorerStore(sessionType)
  const { openPreview } = useCitationPreview()
  const resolvedCitationSection = citationSection ?? sessionType

  const deepState = explorerStore((state) => getDeepStateForSession(state, state.currentSessionId))
  const replaceDeepState = explorerStore((state) => state.replaceDeepState)
  const setDeepPlanProposed = explorerStore((state) => state.setDeepPlanProposed)
  const introDismissedWhileIdle = explorerStore((state) => state.deepModeIntroDismissedWhileIdle)
  const setDeepModeIntroDismissedWhileIdle = explorerStore(
    (state) => state.setDeepModeIntroDismissedWhileIdle
  )

  const {
    todos,
    files,
    activeFile,
    planProposed,
    planApproved,
    planSnapshot,
    proposedTodos,
    askUserProposed,
    askUserPayload,
    subagentActive,
    activityLog,
    sources,
    token_budget,
    deliverableType,
    verificationSummary,
    citationVerifications,
    liveSubagentReports,
    orchestratorReasoning,
    subagentReasoning,
  } = deepState

  const isStreaming = explorerStore((state) => {
    const currentId = state.currentSessionId
    const session = currentId ? state.sessionStates[currentId] : undefined
    return session?.isStreaming ?? false
  })

  const isLoading = explorerStore((state) => {
    const currentId = state.currentSessionId
    const session = currentId ? state.sessionStates[currentId] : undefined
    return session?.isLoading ?? false
  })

  const resolvedSessionTitle = sessionTitle || t("deepExplorer.default_session_title")
  const [activityTab, setActivityTab] = useState<"activity" | "sources">("activity")

  // ── Viewer hero state ─────────────────────────────────────────────────────
  // Holds the currently-previewed citation when the user clicks a source or
  // deliverable `[[...]]`. Transient — lives for the session, not persisted.
  const [viewerPreview, setViewerPreview] = useState<ViewerPreview | null>(null)

  // ── Hero / heroLocked state ───────────────────────────────────────────────
  const currentSessionId = explorerStore((state) => state.currentSessionId)
  const sessionMessages = explorerStore((state) => {
    if (!currentSessionId) return undefined
    if (!(currentSessionId in state.sessionStates)) return undefined
    return state.sessionStates[currentSessionId].messages
  })
  const streamingResponse = explorerStore((state) => {
    if (!currentSessionId) return undefined
    if (!(currentSessionId in state.sessionStates)) return undefined
    return state.sessionStates[currentSessionId].streamingAnswer?.[1]
  })
  const heroStorageKey = currentSessionId ? `deep-hero-id:${currentSessionId}` : null

  const [heroId, setHeroIdState] = useState<HeroId>(() => {
    if (heroStorageKey) {
      const saved = readJsonFromStorage<HeroId>(heroStorageKey)
      // Viewer is transient (no viewerPreview on reload), so we don't
      // restore it — fall through to the plan default.
      if (saved === "plan" || saved === "activity" || saved === "files") {
        // Phase 1 (workspace-focus): the Plan hero is suppressed in workspace
        // mode because the center artifact owns plan approval. Coerce a
        // persisted "plan" heroId to "activity" so the panel never opens to
        // an empty/dead Plan section.
        // Phase 3 (workspace-focus): the Files hero is also suppressed —
        // /plan.md and /deliverable.md are now center artifacts, so the
        // peek's Files rail entry is redundant. Coerce persisted "files"
        // to "activity" for the same reason as "plan" above.
        if (inWorkspaceMode && (saved === "plan" || saved === "files")) {
          return "activity"
        }
        return saved
      }
    }
    return inWorkspaceMode ? "activity" : "plan"
  })
  const [heroLocked, setHeroLocked] = useState(false)

  const setHeroId = useCallback(
    (id: HeroId, manual = false) => {
      setHeroIdState(id)
      if (manual) setHeroLocked(true)
      if (heroStorageKey) writeJsonToStorage(heroStorageKey, id)
    },
    [heroStorageKey]
  )

  // ── Auto-hero logic ───────────────────────────────────────────────────────
  const hasDeliverable = files.some(
    (f) => f.path === "/deliverable.md" && f.content.trim().length > 0
  )
  const deepRunActive = useMemo(
    () =>
      isLoading ||
      isStreaming ||
      planProposed ||
      askUserProposed ||
      Boolean(subagentActive) ||
      todos.some((t) => t.status !== "completed"),
    [isLoading, isStreaming, planProposed, askUserProposed, subagentActive, todos]
  )

  useEffect(() => {
    if (heroLocked) return
    // Phase 3 (workspace-focus): in workspace mode the Plan and Files heroes
    // are suppressed (their content lives in the center workspace as the
    // plan / deliverable artifacts). Activity is the only telemetry hero
    // that should ever auto-mount, so short-circuit before the legacy
    // plan-ready / deliverable-ready branches can flip us elsewhere.
    if (inWorkspaceMode) {
      setHeroIdState("activity")
      return
    }
    if (planProposed) {
      // Switch to Files hero so the user sees /plan.md rendered alongside
      // VirtualFileViewer's approve/reject buttons. Falls back to Plan hero
      // if the plan file hasn't arrived in the snapshot yet.
      const planFileReady = files.some((f) => f.path === "/plan.md")
      setHeroIdState(planFileReady ? "files" : "plan")
    } else if (deepRunActive && !hasDeliverable) {
      setHeroIdState("activity")
    } else if (hasDeliverable) {
      setHeroIdState("files")
    }
  }, [heroLocked, planProposed, deepRunActive, hasDeliverable, files, inWorkspaceMode])

  // Auto-select an artifact when one becomes relevant so the Files hero
  // renders it instead of the "no file open" skeleton. Priority:
  // deliverable (post-run) > plan (while awaiting approval).
  useEffect(() => {
    if (activeFile || !currentSessionId) return
    const deliverableExists = files.some(
      (f) => f.path === "/deliverable.md" && f.content.trim().length > 0
    )
    if (deliverableExists) {
      replaceDeepState(currentSessionId, { activeFile: "/deliverable.md" })
      return
    }
    if (planProposed && files.some((f) => f.path === "/plan.md")) {
      replaceDeepState(currentSessionId, { activeFile: "/plan.md" })
    }
  }, [activeFile, files, planProposed, replaceDeepState, currentSessionId])

  // Reset heroLocked when a new run completes (deepRunActive falls to false).
  // Intro overlay dismissal is session-scoped via sessionStorage — do NOT
  // re-raise it after each run.
  const prevDeepRunActive = useRef(false)
  useEffect(() => {
    if (prevDeepRunActive.current && !deepRunActive) {
      setHeroLocked(false)
    }
    prevDeepRunActive.current = deepRunActive
  }, [deepRunActive])

  const showOnboarding = !deepRunActive && !introDismissedWhileIdle

  const handleDismissOnboarding = useCallback(() => {
    setDeepModeIntroDismissedWhileIdle(true)
  }, [setDeepModeIntroDismissedWhileIdle])

  const activeFileObj = files.find((f) => f.path === activeFile) ?? null

  const handleFileSelect = useCallback(
    (path: string) => {
      if (!currentSessionId) return
      replaceDeepState(currentSessionId, { activeFile: path })
    },
    [replaceDeepState, currentSessionId]
  )

  const handleExport = useCallback(() => {
    exportScratchpad({ todos, files, sessionTitle: resolvedSessionTitle })
  }, [todos, files, resolvedSessionTitle])

  /**
   * Open a citation preview inside the Deep panel's Viewer hero instead of
   * the main content sidebar. Keeps the plan / activity / files context
   * visible while the user inspects a source. "Open in sidebar" in the
   * viewer header is the escape hatch when they want more real estate.
   */
  const openInlinePreview = useCallback(
    (
      citation: string,
      citationSec: string,
      options?: { chunkText?: string; documents?: [string, Document][] }
    ) => {
      let resolved: ReturnType<typeof resolveCitationPreview>
      try {
        resolved = resolveCitationPreview(citation, citationSec, options)
      } catch (err) {
        logger.error("Failed to resolve citation for inline viewer", err as Error)
        // Fall back to the main sidebar — at minimum the user still gets a
        // preview even if our inline path choked on a malformed citation.
        openPreview(citation, citationSec, options)
        return
      }
      setViewerPreview({
        state: resolved.state,
        title: resolved.title,
        citation,
        citationSection: citationSec,
        documents: options?.documents,
      })
      setHeroId("viewer", true)
    },
    [openPreview, setHeroId]
  )

  /** Escape hatch from the inline viewer to the full-size content sidebar. */
  const handleOpenInSidebar = useCallback(() => {
    if (!viewerPreview) return
    const sidebarPreview = sidebarPreviewArguments(
      viewerPreview.citation,
      viewerPreview.citationSection,
      viewerPreview.documents
    )
    openPreview(sidebarPreview.citation, sidebarPreview.section, sidebarPreview.options)
  }, [openPreview, viewerPreview])

  const handleCloseViewer = useCallback(() => {
    setViewerPreview(null)
    // Bounce back to Activity — it's where the user most likely came
    // from when clicking a source. Any further navigation is one rail
    // click away.
    setHeroId("activity", true)
  }, [setHeroId])

  const categorized = useMemo(() => categorizeFiles(files), [files])
  const artifactCount = categorized.artifacts.length
  const sourcesCount = categorized.sources.length

  // Saved documents outlive the 256 KiB activity narrative. The Sources tab
  // merges every turn's rows with activity cards (activity wins on the same
  // document). Citation opens do not use this list: an alias is only unique
  // inside the turn that minted it.
  const savedCitationDocuments = useMemo(() => {
    const documents: [string, Document][] = []
    for (const [, response] of sessionMessages ?? []) {
      const persisted = response.context.documents
      if (persisted?.length) documents.push(...persisted)
    }
    return documents
  }, [sessionMessages])

  const sourceCardsFromActivity = useMemo(
    () =>
      mergeSourceCards(
        collectSourceCards(activityLog),
        sourceCardsFromSavedDocuments(savedCitationDocuments)
      ),
    [activityLog, savedCitationDocuments]
  )

  // One scope per answer. The active deliverable file picks the answer that
  // wrote it; the click below must not search an earlier turn's alias.
  const citationAnswers = useMemo(
    () =>
      (sessionMessages ?? []).map(([, response]) => ({
        activityLog: citationActivityFromSaved(response.context.deep_activity_events),
        documents: response.context.documents,
        artifacts: response.context.artifacts,
      })),
    [sessionMessages]
  )
  const deliverableCitationScope = useMemo(
    () =>
      selectDeliverableCitationScope({
        path: activeFile,
        answers: citationAnswers,
        activityLog,
        liveDocuments: streamingResponse?.context.documents,
      }),
    [activeFile, activityLog, citationAnswers, streamingResponse]
  )

  // ── Token budget ──────────────────────────────────────────────────────────
  const tokenBudgetUsage =
    token_budget && token_budget.total > 0 ? token_budget.used / token_budget.total : 0
  const tokenBudgetPercent = Math.round(tokenBudgetUsage * 100)
  const hasTokenBudgetUsage = Boolean(
    token_budget && token_budget.total > 0 && token_budget.used > 0
  )

  // ── Activity pulse ────────────────────────────────────────────────────────
  const prevActivityCount = useRef(activityLog.length)
  const [activityPulse, setActivityPulse] = useState(false)
  useEffect(() => {
    if (activityLog.length > prevActivityCount.current) {
      setActivityPulse(true)
      const timer = setTimeout(() => setActivityPulse(false), 2500)
      prevActivityCount.current = activityLog.length
      return () => clearTimeout(timer)
    }
  }, [activityLog.length])

  // ── Save to Canvas ─────────────────────────────────────────────────────────
  const [savingToCanvas, setSavingToCanvas] = useState(false)

  const canvasDocStorageKey = currentSessionId ? `deep-canvas-doc:${currentSessionId}` : null
  const [savedCanvasDocumentId, setSavedCanvasDocumentId] = useState<string | null>(null)

  useEffect(() => {
    if (!currentSessionId || !canvasDocStorageKey) {
      setSavedCanvasDocumentId(null)
      return
    }
    let restored: string | null = null
    try {
      restored = localStorage.getItem(canvasDocStorageKey)
    } catch {
      restored = null
    }
    if (!restored && sessionMessages && sessionMessages.length > 0) {
      for (let i = sessionMessages.length - 1; i >= 0; i--) {
        const [, response] = sessionMessages[i]
        const persisted = response.context.canvas_document_id
        if (typeof persisted === "string" && persisted.length > 0) {
          restored = persisted
          break
        }
      }
    }
    setSavedCanvasDocumentId(restored ?? null)
  }, [currentSessionId, canvasDocStorageKey, sessionMessages])

  const persistCanvasDocId = useCallback(
    (documentId: string | null) => {
      setSavedCanvasDocumentId(documentId)
      if (!canvasDocStorageKey) return
      try {
        if (documentId) {
          localStorage.setItem(canvasDocStorageKey, documentId)
        } else {
          localStorage.removeItem(canvasDocStorageKey)
        }
      } catch {
        // localStorage may be unavailable or full — silently ignore.
      }
    },
    [canvasDocStorageKey]
  )

  const handleSaveToCanvas = useCallback(async () => {
    const deliverable = files.find((f) => f.path === "/deliverable.md") ?? null
    const plan = files.find((f) => f.path === "/plan.md") ?? null
    type CitationVerdict = DeepExplorerSessionState["citationVerifications"][string]
    const verifications = (
      Object.entries(citationVerifications) as Array<[string, CitationVerdict]>
    ).map(([citation, verdict]) => ({
      citation,
      verified: verdict.verified,
      issue: verdict.issue,
    }))
    const { title, content } = buildCanvasContent({
      question: resolvedSessionTitle,
      deliverable,
      plan,
      todos,
      activityLog,
      sources: collectSourceCards(activityLog),
      verifications,
      verificationSummary,
    })
    if (!content.trim()) {
      toast.error(t("deepExplorer.save_to_canvas_no_deliverable"))
      return
    }
    const sourceSessionId =
      typeof currentSessionId === "string" && currentSessionId.length > 0
        ? currentSessionId
        : undefined
    setSavingToCanvas(true)
    try {
      const settingsService = SettingsService.getInstance()
      let doc = null as Awaited<ReturnType<typeof createDocumentApi>> | null
      let wasUpdate = false
      if (savedCanvasDocumentId) {
        try {
          doc = await updateDocumentApi(
            savedCanvasDocumentId,
            {
              title,
              content,
              contentFormat: "markdown",
              edit_source: "ai",
              ...(sourceSessionId ? { sourceSessionId } : {}),
            },
            settingsService.token
          )
          wasUpdate = true
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err)
          const isMissing = /\b(404|403)\b/.test(message)
          if (!isMissing) throw err
          logger.info(
            `Linked Canvas doc ${savedCanvasDocumentId} missing (${message}); creating a new one.`
          )
          persistCanvasDocId(null)
        }
      }
      if (!doc) {
        doc = await createDocumentApi(
          {
            title,
            content,
            contentFormat: "markdown",
            ...(sourceSessionId ? { sourceSessionId } : {}),
          },
          settingsService.token
        )
        persistCanvasDocId(doc.id)
      }
      openCanvasInSidebar(
        {
          documentId: doc.id,
          activeDocumentCanEdit: true,
          title: doc.title,
          content: doc.content,
          lastSavedAt: doc.updated ?? null,
        },
        { title: doc.title }
      )
      toast.success(
        wasUpdate
          ? t("deepExplorer.update_canvas_success")
          : t("deepExplorer.save_to_canvas_success")
      )
    } catch (err) {
      logger.error("Save to Canvas failed", err as Error)
      toast.error(t("deepExplorer.save_to_canvas_error"))
    } finally {
      setSavingToCanvas(false)
    }
  }, [
    files,
    resolvedSessionTitle,
    todos,
    activityLog,
    citationVerifications,
    verificationSummary,
    t,
    savedCanvasDocumentId,
    persistCanvasDocId,
    currentSessionId,
  ])

  const canSaveToCanvas = useMemo(
    () =>
      files.some(
        (f) =>
          f.path === "/deliverable.md" &&
          typeof f.content === "string" &&
          f.content.trim().length > 0
      ),
    [files]
  )

  const [saveJustReady, setSaveJustReady] = useState(false)
  const previouslyCanSaveRef = useRef(false)
  useEffect(() => {
    if (canSaveToCanvas && !previouslyCanSaveRef.current) {
      setSaveJustReady(true)
      const timer = setTimeout(() => setSaveJustReady(false), 2500)
      previouslyCanSaveRef.current = true
      return () => clearTimeout(timer)
    }
    if (!canSaveToCanvas) {
      previouslyCanSaveRef.current = false
    }
  }, [canSaveToCanvas])

  // ── Plan decision handler ────────────────────────────────────────────────
  const handlePlanDecision = useCallback(
    (decision: PlanDecision, extra?: { editedContent?: string; feedback?: string }) => {
      if (decision !== "revise" && currentSessionId) {
        if (decision === "approve" && proposedTodos.length > 0) {
          replaceDeepState(currentSessionId, { todos: proposedTodos })
        }
        setDeepPlanProposed(currentSessionId, false)
      }
      // Forward both `feedback` and `editedContent` so the WS sender can
      // attach edited /plan.md content to the approve frame. Without this
      // pass-through the editor's save-and-approve action silently runs the
      // original plan (the user's edits are dropped on the floor).
      onPlanDecision?.(
        decision,
        extra ? { feedback: extra.feedback, editedContent: extra.editedContent } : undefined
      )
    },
    [setDeepPlanProposed, replaceDeepState, proposedTodos, onPlanDecision, currentSessionId]
  )

  const displayTodos =
    planProposed && proposedTodos.length > 0
      ? proposedTodos.map((t) => ({ ...t, status: "pending" as const, started_at: null }))
      : todos
  const doneCount = todos.filter((t) => t.status === "completed").length

  // ── Subagent stats ────────────────────────────────────────────────────────
  const subagentStats = useMemo(() => getSubagentDispatchStats(activityLog), [activityLog])

  // Phase 5.2: scope the rendered timeline to a single mission when set.
  //
  // `DeepActivityEvent` does not yet carry a `mission_id` — and `mission_id`
  // currently equals `thread_id` for every artifact, so all events in the
  // current session already belong to "this mission". Until mission_id has
  // finer granularity (per-turn UUID), the predicate below is intentionally
  // permissive: it accepts events that either (a) have no mission_id (legacy
  // shape — the common case today) or (b) match by mission_id / dispatch_id.
  // The banner still ships in the wrapper so the affordance is testable.
  const filteredActivityLog = useMemo(() => {
    if (!missionFilter) return activityLog
    return activityLog.filter((evt) => {
      const eventMission = (evt as { mission_id?: string | null }).mission_id ?? null
      if (eventMission == null) return true
      return eventMission === missionFilter || evt.dispatch_id === missionFilter
    })
  }, [activityLog, missionFilter])

  // ── Hero section renderers ────────────────────────────────────────────────

  const renderPlanHero = () => (
    <HeroSection>
      <div className="border-border flex h-9 shrink-0 items-center justify-between border-b px-3">
        <span className="text-foreground text-xs font-medium">
          {t("deepExplorer.section_todo")}
          {todos.length > 0 ? ` · ${doneCount}/${todos.length}` : ""}
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-1">
        <TodoList todos={displayTodos} />
      </div>
    </HeroSection>
  )

  const renderActivityHero = () => (
    <HeroSection>
      <div
        className="border-border flex h-9 shrink-0 items-center gap-3 border-b px-3"
        role="tablist"
      >
        <button
          aria-selected={activityTab === "activity"}
          role="tab"
          type="button"
          className={cn(
            "text-xs font-medium transition-colors",
            activityTab === "activity"
              ? "text-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
          onClick={() => setActivityTab("activity")}
        >
          {t("deepExplorer.section_activity")}
        </button>
        <button
          aria-selected={activityTab === "sources"}
          role="tab"
          type="button"
          className={cn(
            "text-xs font-medium transition-colors",
            activityTab === "sources"
              ? "text-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
          onClick={() => setActivityTab("sources")}
        >
          {t("deepExplorer.section_sources")}
          {sources.length > 0 ? ` (${sources.length})` : ""}
        </button>
        {/* UI-5: Rewind to plan — visible only after the user has approved
            and a snapshot exists. Restoring opens the plan editor again so
            the user can switch execution_mode or edit todos and re-approve
            without starting a new chat turn. */}
        {planApproved && planSnapshot && onRewindToPlan && !hasDeliverable && (
          <button
            className="text-muted-foreground hover:text-foreground hover:bg-muted ml-auto rounded-md px-2 py-0.5 text-[11px] font-medium transition-colors"
            type="button"
            title={t("deepExplorer.rewind_tooltip", {
              defaultValue: "Stop and return to the approved plan editor.",
            })}
            onClick={onRewindToPlan}
          >
            {t("deepExplorer.rewind_to_plan", { defaultValue: "Rewind to plan" })}
          </button>
        )}
      </div>
      {activityTab === "activity" && <ActiveWorkersRail events={filteredActivityLog} />}
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-2">
        {activityTab === "activity" ? (
          <ActivityLog
            autoExpandOrchestrator={deepRunActive && !planApproved}
            events={filteredActivityLog}
            liveSubagentReports={liveSubagentReports}
            orchestratorReasoning={orchestratorReasoning}
            subagentReasoning={subagentReasoning}
          />
        ) : (
          <SourceRibbon
            activityLog={activityLog}
            citationSection={resolvedCitationSection}
            legacySources={sources}
            sourceCards={sourceCardsFromActivity}
            verificationSummary={verificationSummary}
            onOpenPreview={openInlinePreview}
          />
        )}
      </div>
      {/* UI-6: push-side note input. Surfaces only while a run is active and
          the plan has been approved — before approval, the user has the
          regular Approve/Edit/Revise affordances; after the deliverable
          lands, the input would just generate noise. */}
      {onSendUserNote && (
        <BtwInput
          enabled={
            planApproved &&
            !planProposed &&
            !askUserProposed &&
            !hasDeliverable &&
            (isLoading || isStreaming || Boolean(subagentActive))
          }
          onSend={onSendUserNote}
        />
      )}
    </HeroSection>
  )

  const fileHeaderActions = (
    <>
      <Button
        disabled={!canSaveToCanvas || savingToCanvas}
        size="icon"
        variant="ghost"
        className={cn(
          "text-muted-foreground hover:text-foreground size-6 shrink-0",
          canSaveToCanvas && "text-primary",
          saveJustReady &&
            !savedCanvasDocumentId &&
            "ring-primary/60 ring-2 motion-safe:animate-pulse"
        )}
        title={t(
          savedCanvasDocumentId ? "deepExplorer.update_canvas" : "deepExplorer.save_to_canvas"
        )}
        onClick={handleSaveToCanvas}
      >
        <FileOutput className="size-3.5" />
      </Button>
      <Button
        className="text-muted-foreground hover:text-foreground size-6 shrink-0"
        size="icon"
        title={t("deepExplorer.export_markdown")}
        variant="ghost"
        onClick={handleExport}
      >
        <Download className="size-3.5" />
      </Button>
    </>
  )

  const renderFilesHero = () => (
    <HeroSection>
      {files.length > 0 && (
        <div className="border-border flex shrink-0 items-center gap-1 border-b px-2 py-1">
          <VirtualFileBrowser
            activeFile={activeFile}
            files={files}
            onFileSelect={handleFileSelect}
          />
        </div>
      )}
      <VirtualFileViewer
        citationSection={resolvedCitationSection}
        deliverableType={deliverableType as never}
        file={activeFileObj}
        headerActions={fileHeaderActions}
        isStreaming={isStreaming}
        planProposed={planProposed}
        onApproveInBackground={onApproveInBackground}
        onPlanDecision={handlePlanDecision}
        onCitationClick={(citation) => {
          // Web URL citations open in a new tab — they aren't backed by an
          // internal document, so routing them through `/api/content/...` 404s.
          // Mirrors the regular Explorer's `isUrl` branch in
          // `system-message-actions.tsx`.
          if (citation.startsWith("http://") || citation.startsWith("https://")) {
            if (typeof window !== "undefined") {
              window.open(citation, "_blank", "noopener,noreferrer")
            }
            return
          }
          // Repair a bare filename against this answer's cards only. The
          // same alias in an earlier turn is a different document.
          const resolvedCitation = resolveCitationWithSources(
            citation,
            deliverableCitationScope.cards
          )
          // Open the file inline in the Viewer hero — deep panel stays
          // visible so plan/activity/sources remain accessible while
          // reading the source. User can still "Open in sidebar" from the
          // viewer header when they want more room.
          openInlinePreview(resolvedCitation, resolvedCitationSection, {
            documents: deliverableCitationScope.documents,
          })
          // Re-fire the hover event so SourceRibbon's existing listener
          // highlights the matching card in the Sources tab. Use the
          // repaired citation so its matchKeys (`library:filename`) line up.
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("deep:citation-hover", { detail: { citation: resolvedCitation } })
            )
          }
        }}
        onCitationViewDocument={(citation, citationSec, options) => {
          // Hover-peek "Open" routes to the same inline viewer as a click —
          // except web URLs, which open externally instead of trying to
          // render in the inline document viewer.
          if (citation.startsWith("http://") || citation.startsWith("https://")) {
            if (typeof window !== "undefined") {
              window.open(citation, "_blank", "noopener,noreferrer")
            }
            return
          }
          const resolvedCitation = resolveCitationWithSources(
            citation,
            deliverableCitationScope.cards
          )
          // The hover-peek carries only a chunk of text. Documents come from
          // the answer that contains this deliverable, not from earlier turns
          // that reused the alias.
          openInlinePreview(resolvedCitation, citationSec, {
            ...options,
            documents: deliverableCitationScope.documents,
          })
        }}
        onSourcesRequested={() => {
          setActivityTab("sources")
          setHeroId("activity", true)
        }}
      />
    </HeroSection>
  )

  // ── Viewer hero (inline citation preview) ───────────────────────────────
  //
  // When the user clicks a SourceCard or a `[[...]]` in the deliverable, we
  // resolve the citation locally and render `CitationViewerContent` inside
  // the Deep panel instead of swapping the panel out for the main content
  // sidebar. "Open in sidebar" escape-hatches to the full viewer when the
  // file deserves more real estate.
  const renderViewerHero = () => {
    if (!viewerPreview) {
      return (
        <HeroSection>
          <div className="border-border flex h-9 shrink-0 items-center gap-2 border-b px-3">
            <BookOpen className="text-muted-foreground size-3.5 shrink-0" />
            <span className="text-foreground text-xs font-medium">
              {t("deepExplorer.section_viewer", { defaultValue: "Source viewer" })}
            </span>
          </div>
          <div className="text-muted-foreground flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-6 text-center text-xs">
            <BookOpen className="size-8 opacity-40" />
            <p>
              {t("deepExplorer.viewer_empty_hint", {
                defaultValue:
                  "Click a source in the Sources tab or a citation in the deliverable to preview it here.",
              })}
            </p>
          </div>
        </HeroSection>
      )
    }
    const { state, title } = viewerPreview
    return (
      <HeroSection>
        <div className="border-border flex h-9 shrink-0 items-center gap-2 border-b px-3">
          <BookOpen className="text-muted-foreground size-3.5 shrink-0" />
          <span
            className="text-foreground min-w-0 flex-1 truncate text-xs font-medium"
            title={title}
          >
            {title || t("deepExplorer.section_viewer", { defaultValue: "Source viewer" })}
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded p-1 transition-colors focus-visible:ring-2 focus-visible:outline-none"
                type="button"
                aria-label={t("deepExplorer.viewer_open_in_sidebar", {
                  defaultValue: "Open in sidebar",
                })}
                onClick={handleOpenInSidebar}
              >
                <ExternalLink className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {t("deepExplorer.viewer_open_in_sidebar", { defaultValue: "Open in sidebar" })}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label={t("deepExplorer.viewer_close", { defaultValue: "Close viewer" })}
                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded p-1 transition-colors focus-visible:ring-2 focus-visible:outline-none"
                type="button"
                onClick={handleCloseViewer}
              >
                <XIcon className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {t("deepExplorer.viewer_close", { defaultValue: "Close viewer" })}
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="min-h-0 flex-1 overflow-hidden">
          <CitationViewerContent
            chunkSnippet={state.chunkSnippet}
            documentUrl={state.documentUrl}
            isUnresolvedSource={state.isUnresolvedSource}
            originalDocumentUrl={state.originalDocumentUrl}
            quoteSnippet={state.quoteSnippet}
            showModifiedPreviewBanner={state.showModifiedPreviewBanner}
          />
        </div>
      </HeroSection>
    )
  }

  // ── Strip renderers ───────────────────────────────────────────────────────

  const renderViewerStrip = () => {
    if (!viewerPreview) return null
    return (
      <Strip onClick={() => setHeroId("viewer", true)}>
        <BookOpen className="size-3.5 shrink-0" />
        <span className="font-medium">
          {t("deepExplorer.section_viewer", { defaultValue: "Source viewer" })}
        </span>
        <span className="text-muted-foreground/70 min-w-0 truncate">· {viewerPreview.title}</span>
      </Strip>
    )
  }

  const renderPlanStrip = () => {
    const nextTask = todos.find((t) => t.status === "in_progress" || t.status === "pending")
    const nextLabel = nextTask?.content ?? nextTask?.text ?? ""
    return (
      <Strip onClick={() => setHeroId("plan", true)}>
        <span className="font-medium">{t("deepExplorer.section_todo")}</span>
        <span className="text-muted-foreground">
          {todos.length > 0 ? `${doneCount}/${todos.length}` : ""}
        </span>
        {nextLabel && (
          <span className="text-muted-foreground/70 min-w-0 truncate">→ {nextLabel}</span>
        )}
      </Strip>
    )
  }

  const renderActivityStrip = () => (
    <Strip pulseDot={activityPulse} onClick={() => setHeroId("activity", true)}>
      <span className="font-medium">{t("deepExplorer.section_activity")}</span>
      <span className="text-muted-foreground/70">
        {activityLog.length > 0
          ? t("deepExplorer.activity_strip_events", { count: activityLog.length })
          : ""}
        {sources.length > 0
          ? ` · ${t("deepExplorer.activity_strip_sources", { count: sources.length })}`
          : ""}
      </span>
    </Strip>
  )

  const renderFilesStrip = () => (
    <Strip
      pulseDot={saveJustReady}
      trailingAction={
        canSaveToCanvas ? (
          <Button
            disabled={savingToCanvas}
            size="sm"
            variant="ghost"
            className={cn(
              "text-primary hover:text-primary/80 ml-auto h-6 px-2 text-xs",
              saveJustReady &&
                !savedCanvasDocumentId &&
                "animate-ready-pulse motion-reduce:animate-none"
            )}
            onClick={() => {
              void handleSaveToCanvas()
            }}
          >
            {savedCanvasDocumentId
              ? t("deepExplorer.update_canvas", { defaultValue: "Update" })
              : t("deepExplorer.save_to_canvas", { defaultValue: "Save" })}
          </Button>
        ) : undefined
      }
      onClick={() => setHeroId("files", true)}
    >
      <FileText className="size-3.5 shrink-0" />
      <span className="font-medium">
        {artifactCount > 0
          ? t("deepExplorer.section_files") + ` · ${artifactCount + sourcesCount}`
          : t("deepExplorer.section_files")}
      </span>
    </Strip>
  )

  // ── Non-hero sections ─────────────────────────────────────────────────────

  const renderNonHeroStrips = () => {
    // Viewer is conditional — only render its strip when a citation has
    // been opened. Everything else always gets a strip when it's not
    // the active hero.
    // Phase 1 (workspace-focus): the Plan section is suppressed in workspace
    // mode because plan approval lives in the center workspace artifact —
    // drop "plan" from the candidate list so the strip row collapses cleanly.
    // Phase 3 (workspace-focus): also drop "files" — /plan.md and
    // /deliverable.md are center artifacts now, so the Files strip would
    // duplicate workspace content. Activity is the only non-hero strip
    // candidate left in workspace mode.
    const baseCandidates: HeroId[] = inWorkspaceMode ? ["activity"] : ["plan", "activity", "files"]
    const candidates: HeroId[] = viewerPreview ? [...baseCandidates, "viewer"] : baseCandidates
    return candidates
      .filter((id) => id !== heroId)
      .map((id) => {
        if (id === "plan") return <span key="plan">{renderPlanStrip()}</span>
        if (id === "activity") return <span key="activity">{renderActivityStrip()}</span>
        if (id === "viewer") return <span key="viewer">{renderViewerStrip()}</span>
        return <span key="files">{renderFilesStrip()}</span>
      })
  }

  // ── Rail ──────────────────────────────────────────────────────────────────

  const renderRail = () => (
    <div
      aria-label={t("deepExplorer.panel_title")}
      className={cn(
        "border-border bg-card flex w-12 shrink-0 flex-col items-center gap-2 border-l p-1.5"
      )}
    >
      {/* Plan ring — hidden in workspace mode; the center artifact owns
          plan approval there. */}
      {!inWorkspaceMode && (
        <RailButton
          active={heroId === "plan"}
          label={t("deepExplorer.section_todo")}
          onClick={() => setHeroId("plan", true)}
        >
          <PlanRing
            active={heroId === "plan"}
            done={doneCount}
            proposed={planProposed}
            total={todos.length}
          />
        </RailButton>
      )}

      {/* Activity dot */}
      <RailButton
        active={heroId === "activity"}
        label={t("deepExplorer.section_activity")}
        pulseDot={activityPulse && heroId !== "activity"}
        onClick={() => setHeroId("activity", true)}
      >
        <Zap className="size-4" />
      </RailButton>

      {/* Subagent chip — hide when 0 */}
      {subagentStats.active > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                "bg-primary/10 text-primary animate-pulse motion-reduce:animate-none",
                "flex h-5 w-10 items-center justify-center rounded-md text-[10px] font-bold tabular-nums"
              )}
            >
              {subagentStats.active > 9 ? "9+" : subagentStats.active}
            </span>
          </TooltipTrigger>
          <TooltipContent side="left">
            {t("deepExplorer.subagent_count_active", {
              active: subagentStats.active,
              used: subagentStats.used,
              defaultValue: "{{active}}/{{used}} agents",
            })}
          </TooltipContent>
        </Tooltip>
      )}

      {/* Files — hidden in workspace mode; /plan.md and /deliverable.md
          are now center workspace artifacts (plan + document), so the
          Files rail entry would just duplicate the canonical surface. */}
      {!inWorkspaceMode && (
        <RailButton
          active={heroId === "files"}
          badge={artifactCount > 0 ? artifactCount : null}
          label={t("deepExplorer.section_files")}
          pulseDot={saveJustReady && heroId !== "files"}
          onClick={() => setHeroId("files", true)}
        >
          <FileText className="size-4" />
        </RailButton>
      )}

      {/* Export — the classic layout offers it in the Files header, which
          workspace mode hides, so the rail carries it there instead. */}
      {inWorkspaceMode && artifactCount > 0 && (
        <RailButton label={t("deepExplorer.export_markdown")} onClick={handleExport}>
          <Download className="size-4" />
        </RailButton>
      )}

      {/* Viewer — only surfaces in the rail once the user has opened a
          source. Keeps the rail uncluttered until it's useful. */}
      {viewerPreview && (
        <RailButton
          active={heroId === "viewer"}
          label={t("deepExplorer.section_viewer", { defaultValue: "Source viewer" })}
          onClick={() => setHeroId("viewer", true)}
        >
          <BookOpen className="size-4" />
        </RailButton>
      )}

      {/* Token bar — push to bottom via flex spacer */}
      <span className="flex-1" />

      {hasTokenBudgetUsage && (
        <TokenBar
          label={t("deepExplorer.budget_used", { pct: tokenBudgetPercent })}
          percent={tokenBudgetPercent}
          usageRatio={tokenBudgetUsage}
        />
      )}

      {/* Divider */}
      <span className="border-border w-6 border-t" />

      {/* Narration + Fact-check */}
      <NarrationToggle sessionType={sessionType} />
      <FactCheckToggle sessionType={sessionType} />
    </div>
  )

  // ── Active indicator on rail inner edge ───────────────────────────────────
  // Each RailButton signals active state via bg-primary/10; no extra overlay needed.

  return (
    <>
      <div
        aria-label={t("deepExplorer.panel_title")}
        className="bg-card relative flex h-full min-h-0 flex-row overflow-hidden"
        role="complementary"
      >
        {showOnboarding && <OnboardingOverlay onDismiss={handleDismissOnboarding} />}

        {/* Main content column */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {/* Hero — must be a flex container so HeroSection's flex-1 / min-h-0
              chain actually propagates to the inner overflow-y-auto body.
              Without `flex flex-col` the HeroSection sat in a block parent with
              no resolved height, so the body's `overflow-y-auto` never
              triggered and the side panel couldn't scroll. */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {heroId === "plan" && !inWorkspaceMode && renderPlanHero()}
            {heroId === "activity" && renderActivityHero()}
            {heroId === "files" && !inWorkspaceMode && renderFilesHero()}
            {heroId === "viewer" && renderViewerHero()}
          </div>

          {/* Strips for non-hero sections */}
          <div className="shrink-0">{renderNonHeroStrips()}</div>
        </div>

        {/* 48px rail */}
        {renderRail()}
      </div>

      {/* Phase 2 (workspace-focus): suppress the legacy paged modal when the
          panel is rendered inside the workspace shell. The center-workspace
          `AskUserRenderer` owns the form in that mode; rendering both
          simultaneously would double-prompt the user. Legacy
          (`WORKSPACE_LAYOUT_ENABLED=false`) keeps the modal flow intact. */}
      {!inWorkspaceMode && (
        <AskUserModal
          open={askUserProposed}
          payload={askUserPayload}
          onSkip={() => {
            onAskUserResponse?.({}, currentSessionId, askUserPayload?.ask_id)
          }}
          onSubmit={(answers) => {
            onAskUserResponse?.(answers, currentSessionId, askUserPayload?.ask_id)
          }}
        />
      )}
    </>
  )
}
